import dayjs from 'dayjs'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { API } from '@/api'
import { message } from '@/components'
import { Loading, Trans } from '@/components'
import { ActionMenu } from '@/components/base/ActionMenu'
import { CollapseTableRow } from '@/components/CollapseTableRow'
import { EmptyView } from '@/components/EmptyView.tsx'
import { getDeployCreatePath, useUnifiedContext } from '@/contexts/UnifiedContext'
import { Deployment } from '@/models/deployment.ts'
import { DeploymentBill } from '@/models/deploymentBill.ts'
import { InstanceStatus, RunningStatuses } from '@/models/instance.ts'
import DeploymentDetail from '@/pages/auto-deploy/components/DeploymentDetail.tsx'
import { formatPrice } from '@/utils/formatter.ts'
import { cn } from '@/utils/style.ts'

interface AutoDeploymentTableProps {
  _deploymentList: Deployment[]
  _deploymentBills: Map<string, DeploymentBill[]>
  _fetchDeploymentList: (page: number, showLoading: boolean) => void
  _status?: string
  _enabled?: boolean
  _loading?: boolean
}

export default function AutoDeploymentTable({
  _deploymentList,
  _deploymentBills,
  _status,
  _enabled,
  _loading,
  _fetchDeploymentList,
}: AutoDeploymentTableProps) {
  const navigate = useNavigate()
  const { t } = useTranslation('autoDeploy')
  const ctx = useUnifiedContext()
  const deployCreatePath = getDeployCreatePath(ctx)

  // Memoize instance status map for all deployments
  const instanceStatusMap = useMemo(() => {
    const map = new Map<string, number>()
    _deploymentList.forEach((deploy) => {
      const bills = _deploymentBills.get(deploy.resID) || []
      map.set(deploy.resID, bills.length > 0 ? bills[0].instStatus : -1)
    })
    return map
  }, [_deploymentList, _deploymentBills])

  // Memoize running status map for all deployments
  const runningStatusMap = useMemo(() => {
    const map = new Map<string, boolean>()
    _deploymentList.forEach((deploy) => {
      const status = instanceStatusMap.get(deploy.resID) || -1
      map.set(deploy.resID, RunningStatuses.includes(status))
    })
    return map
  }, [_deploymentList, instanceStatusMap])

  // Memoize last running time map for all deployments
  const lastRunningTimeMap = useMemo(() => {
    const map = new Map<string, string>()
    _deploymentList.forEach((deploy) => {
      const bills = _deploymentBills.get(deploy.resID) || []
      map.set(
        deploy.resID,
        bills.length > 0
          ? dayjs.unix(bills[0].createTime.seconds).format('YYYY-MM-DD HH:mm:ss')
          : '-',
      )
    })
    return map
  }, [_deploymentList, _deploymentBills])

  const renderInstancesStatus = useCallback(
    (resID: string) => {
      const instanceStatus = instanceStatusMap.get(resID) || -1

      const statusConfig = new Map<number, { label: React.ReactNode; color: string }>([
        [
          InstanceStatus.Canceling,
          {
            label: (
              <Trans i18nKey="instanceStatus.idle" ns="autoDeploy">
                Idle
              </Trans>
            ),
            color: 'text-warning',
          },
        ],
        [
          InstanceStatus.Pending,
          {
            label: (
              <Trans i18nKey="instanceStatus.idle" ns="autoDeploy">
                Idle
              </Trans>
            ),
            color: 'text-warning',
          },
        ],
        [
          InstanceStatus.Running,
          {
            label: (
              <Trans i18nKey="instanceStatus.running" ns="autoDeploy">
                Running
              </Trans>
            ),
            color: 'text-success',
          },
        ],
        [
          InstanceStatus.Exited,
          {
            label: (
              <Trans i18nKey="instanceStatus.standby" ns="autoDeploy">
                Standby
              </Trans>
            ),
            color: 'text-warning',
          },
        ],
      ])

      const defaultStatus = {
        label: (
          <Trans i18nKey="instanceStatus.standby" ns="autoDeploy">
            Standby
          </Trans>
        ),
        color: 'text-warning',
      }
      const { label, color } = statusConfig.get(instanceStatus) || defaultStatus
      return <div className={color}>{label}</div>
    },
    [instanceStatusMap],
  )

  const getLastRunningTime = useCallback(
    (resID: string): string => {
      return lastRunningTimeMap.get(resID) || '-'
    },
    [lastRunningTimeMap],
  )

  const isInstanceRunning = useCallback(
    (resID: string): boolean => {
      return runningStatusMap.get(resID) || false
    },
    [runningStatusMap],
  )

  const toggleDeployment = useCallback(
    (deployment: Deployment, enabled: boolean) => {
      API.updateDeployment({
        res_id: deployment.resID,
        gtw_res_id: deployment.gateway.resID,
        enabled,
        description: deployment.description,
        hardware_resource: JSON.stringify(deployment.runtimeEnv.hardwareEnv.resource),
        name: deployment.name,
        port: deployment.gateway.port,
        system_env: JSON.stringify(deployment.runtimeEnv.systemEnv),
      }).then((res) => {
        if (res.code === 0) {
          message.success(
            enabled ? t('message.resumeDeploySuccess') : t('message.suspendDeploySuccess'),
          )
          _fetchDeploymentList(1, false)
        }
      })
    },
    [_fetchDeploymentList],
  )

  const handleInstanceStatus = useCallback(
    (deployment: Deployment) => {
      const bills = _deploymentBills.get(deployment.resID) || []
      const running = runningStatusMap.get(deployment.resID) || false

      if (!running) {
        fetch(deployment.gateway.url, { mode: 'no-cors' })
        message.success(t('message.startDeploy'))
      } else {
        API.deleteInstance({
          res_id: bills[0].instResID,
        }).then((res) => {
          if (res.code === 0) {
            message.success(t('message.releaseInstance'))
            _fetchDeploymentList(1, false)
          }
        })
      }
    },
    [_deploymentBills, runningStatusMap, _fetchDeploymentList],
  )

  const handleDelete = useCallback(
    (resID: string) => {
      API.deleteDeployment({
        res_id: resID,
      })
        .then((res) => {
          if (res.code === 0) {
            message.success(t('message.deleteSuccess'))
            _fetchDeploymentList(1, false)
          }
        })
        .catch((err) => {
          console.error(err)
          message.error(t('message.deleteFailed'))
        })
    },
    [_fetchDeploymentList],
  )

  return (
    <>
      {_loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!_loading && _deploymentList.length === 0 && (
        <div className="mt-4">
          <EmptyView
            title={
              <span className="t-sm text-secondary">
                <Trans i18nKey="common.emptyViewNoDataMsg" />
              </span>
            }
          />
        </div>
      )}

      {!_loading && _deploymentList.length > 0 && (
        <table className="table-card mt-4 table-fixed">
          <thead>
            <tr>
              <th colSpan={2}>
                <Trans i18nKey="tableHeaderID" ns="autoDeploy">
                  ID
                </Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="tableHeaderName" ns="autoDeploy">
                  Name
                </Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="tableHeaderStatus" ns="autoDeploy">
                  Status
                </Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="tableHeaderInsStatus" ns="autoDeploy">
                  Instance Status
                </Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="tableHeaderCost" ns="autoDeploy">
                  Cost
                </Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="tableHeaderLastRunTime" ns="autoDeploy">
                  Last Running Time
                </Trans>
              </th>
              <th colSpan={2}>
                <Trans i18nKey="tableHeaderAction" ns="autoDeploy">
                  Action
                </Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {_deploymentList.map((deploy) => (
              <CollapseTableRow
                key={deploy.resID}
                colSpan={14}
                tableClassName={'!border-none ring-1 ring-neutral-200 rounded-xl'}
                content={
                  <DeploymentDetail
                    _deployment={deploy}
                    _deploymentBills={_deploymentBills}
                    _enabled={_enabled}
                    onUpdate={() => _fetchDeploymentList(1, false)}
                  />
                }
              >
                <td colSpan={2} className="t-sm">
                  {deploy.resID}
                </td>
                <td colSpan={2} className="t-sm">
                  {deploy.name}
                </td>
                <td colSpan={2} className="t-sm">
                  <div
                    className={cn(
                      't-s-sb text-primary',
                      _status === 'Activated' ? 'text-active' : 'text-error',
                    )}
                  >
                    {_status === 'Activated' ? (
                      <Trans i18nKey="status.activated" ns="autoDeploy">
                        Activated
                      </Trans>
                    ) : (
                      <Trans i18nKey="status.suspended" ns="autoDeploy">
                        Suspended
                      </Trans>
                    )}
                  </div>
                </td>
                <td colSpan={2} className="t-sm">
                  {renderInstancesStatus(deploy.resID)}
                </td>
                <td colSpan={2} className="t-sm">
                  {formatPrice(Math.abs(deploy.totalCostCent))}
                </td>
                <td colSpan={2} className="t-sm">
                  {getLastRunningTime(deploy.resID)}
                </td>
                <td colSpan={1} className="t-sm">
                  <ActionMenu
                    items={[
                      {
                        label: (
                          <Trans i18nKey="action.edit" ns="autoDeploy">
                            Edit
                          </Trans>
                        ),
                        onClick: () => {
                          navigate(deployCreatePath, {
                            state: {
                              editingDeployment: deploy,
                              isEditingDeployEnabled: _status === 'Activated',
                            },
                          })
                        },
                        type: 'highlight',
                      },
                      {
                        label:
                          _status === 'Activated' ? (
                            <Trans i18nKey="action.suspend" ns="autoDeploy">
                              Suspend
                            </Trans>
                          ) : (
                            <Trans i18nKey="action.resume" ns="autoDeploy">
                              Resume
                            </Trans>
                          ),
                        onClick: () => toggleDeployment(deploy, _status !== 'Activated'),
                        type: 'highlight',
                      },
                      {
                        label: isInstanceRunning(deploy.resID) ? (
                          <Trans i18nKey="action.release" ns="autoDeploy">
                            Release
                          </Trans>
                        ) : (
                          <Trans i18nKey="action.deploy" ns="autoDeploy">
                            Deploy
                          </Trans>
                        ),
                        onClick: () => handleInstanceStatus(deploy),
                        type: _status === 'Activated' ? 'highlight' : 'default',
                        disabled: _status !== 'Activated',
                      },
                      {
                        label: (
                          <Trans i18nKey="action.delete" ns="autoDeploy">
                            Delete
                          </Trans>
                        ),
                        onClick: () => handleDelete(deploy.resID),
                        type: 'danger',
                      },
                    ]}
                    popupContainer="body"
                  />
                </td>
              </CollapseTableRow>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}
